"use client";

import { motion } from "framer-motion";
import { ContactData } from "@/data/types";

interface ContactInfoProps {
  contactInfo: ContactData['contactInfo'];
  socialLinks: ContactData['socialLinks'];
}

export function ContactInfo({ contactInfo, socialLinks }: ContactInfoProps) {
  return (
    <>
      <div className="bg-[rgba(90,21,16,0.2)] backdrop-blur-sm border border-[#ff8855]/20 rounded-xl p-6 md:p-8">
        <h2 className="text-xl font-bold mb-6 text-[#ff8855]">{contactInfo.title}</h2>

        <div className="space-y-6">
          {/* Nur Standort anzeigen - E-Mail entfernt */}
          <motion.div
            className="flex items-start space-x-4"
            whileHover={{ x: 5 }}
          >
            <div className="bg-[rgba(90,21,16,0.5)] p-3 rounded-lg flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5 text-[#ff8855]">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm text-white/60 mb-1">{contactInfo.location.label}</h3>
              <p className="text-white">{contactInfo.location.value}</p>
            </div>
          </motion.div>

          {/* Social Links */}
          <div className="pt-4 mt-6 border-t border-[#ff8855]/20">
            <h3 className="text-sm text-white/60 mb-3">
              {contactInfo.social?.title || "Finde mich auch auf"}
            </h3>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((platform) => (
                <motion.a
                  key={platform.name}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform.name}
                  className="bg-[rgba(90,21,16,0.5)] hover:bg-[rgba(90,21,16,0.8)] text-white p-2.5 rounded-lg flex items-center gap-2"
                  whileHover={{ y: -3 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <span className="text-[#ff8855]">{renderSocialIcon(platform.icon)}</span>
                  <span className="text-sm">{platform.name}</span>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Aufmerksamkeit auf Formular lenken */}
          <motion.div
            className="mt-8 p-4 border border-[#ff8855]/20 rounded-lg bg-[rgba(90,21,16,0.1)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="font-medium text-[#ff8855] mb-2">
              {contactInfo.quickResponse?.title || "Schnelle Antwort gewünscht?"}
            </h3>
            <p className="text-sm text-white/80">
              {contactInfo.quickResponse?.text || "Nutze das Formular und ich melde mich schnellstmöglich bei Dir zurück."}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Dekoratives Element */}
      <motion.div
        className="hidden md:block mt-8 relative h-40"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 right-0 w-32 h-32 text-[#ff8855]/10" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z" />
        </svg>
        <motion.div
          className="absolute top-0 left-0 w-24 h-24 rounded-full bg-[#ff8855]/5 blur-2xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, repeatType: "reverse" }}
        />
      </motion.div>
    </>
  );
}


// Helper-Funktion zum Rendern der Icons
function renderSocialIcon(iconName: string) {
  switch (iconName) {
    case 'linkedin':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
        </svg>
      );
    case 'github':
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
        </svg>
      );
    default:
      return null;
  }
}